import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { mandateService } from '@/services/api';
import {
  clearComposerHandoff,
  storeComposerPrefill,
  storeComposerSeed,
} from '@/features/research/composerPrefill';
import { paths } from '@/routes/paths';
import {
  ATTACH_ACCEPT,
  MAX_ATTACHMENTS,
  MAX_FILE_BYTES,
  MAX_FILE_MB,
  MANDATE_PLACEHOLDER,
  MODE_PATH,
  SEARCH_CHIPS,
} from './constants';

export default function ResearchHomeMandate() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [mandate, setMandate] = useState('');
  const [parsing, setParsing] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [attachError, setAttachError] = useState('');

  const launch = (path: string, prompt?: string) => {
    clearComposerHandoff();
    if (prompt?.trim()) storeComposerSeed(prompt.trim());
    navigate(path);
  };

  const addFiles = (incoming: FileList | null) => {
    if (!incoming || incoming.length === 0) return;
    setAttachError('');
    const next = [...attachments];
    for (const f of Array.from(incoming)) {
      if (f.size > MAX_FILE_BYTES) {
        setAttachError(`"${f.name}" is over ${MAX_FILE_MB}MB and was skipped.`);
        continue;
      }
      if (next.some((e) => e.name === f.name && e.size === f.size)) continue;
      if (next.length >= MAX_ATTACHMENTS) {
        setAttachError(`You can attach up to ${MAX_ATTACHMENTS} files.`);
        break;
      }
      next.push(f);
    }
    setAttachments(next);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (idx: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
    setAttachError('');
  };

  const hasInput = mandate.trim().length > 0 || attachments.length > 0;

  const runMandate = async (text: string, fallbackPath: string = paths.targetList) => {
    const trimmed = text.trim();
    if ((!trimmed && attachments.length === 0) || parsing) return;
    setParsing(true);
    try {
      const prefill = await mandateService.parseMandate({
        text: trimmed,
        files: attachments,
      });
      storeComposerPrefill(prefill);
      navigate(MODE_PATH[prefill.mode ?? 'target'] ?? fallbackPath);
    } catch {
      if (attachments.length > 0) {
        setAttachError(
          "Couldn't read the mandate with the provided context. Try again or remove some items.",
        );
      } else {
        launch(fallbackPath, trimmed);
      }
    } finally {
      setParsing(false);
    }
  };

  return (
    <section className="rh-mandate" aria-labelledby="rh-mandate-heading">
      <button
        type="button"
        className="rh-ai-badge btn-unstyled"
        onClick={() => document.getElementById('rh-mandate-input')?.focus()}
      >
        <i className="bi bi-lightning-fill" aria-hidden="true" />
        Describe it, we build it with AI
      </button>
      <h2 id="rh-mandate-heading" className="rh-mandate-heading">
        Tell us the mandate and we&apos;ll pre populate the criteria for you.
      </h2>

      <div className="rh-mandate-bar">
        <i className="bi bi-magic rh-mandate-spark" aria-hidden="true" />
        <label htmlFor="rh-mandate-input" className="visually-hidden">
          Mandate
        </label>
        <input
          id="rh-mandate-input"
          type="text"
          value={mandate}
          onChange={(e) => setMandate(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void runMandate(mandate);
          }}
          disabled={parsing}
          placeholder={MANDATE_PLACEHOLDER}
          className="rh-mandate-input"
        />
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ATTACH_ACCEPT}
          onChange={(e) => addFiles(e.target.files)}
          className="d-none"
          aria-hidden="true"
          tabIndex={-1}
        />
        <button
          type="button"
          className="rh-icon-btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={parsing || attachments.length >= MAX_ATTACHMENTS}
          title="Attach documents (PDF, Word, Excel, CSV)"
          aria-label="Attach documents"
        >
          <i className="bi bi-paperclip" aria-hidden="true" />
        </button>
        <button
          type="button"
          className="rh-build-btn"
          onClick={() => void runMandate(mandate)}
          disabled={parsing || !hasInput}
        >
          {parsing ? (
            <>
              Reading
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
            </>
          ) : (
            <>
              Build list
              <i className="bi bi-arrow-right" aria-hidden="true" />
            </>
          )}
        </button>
      </div>

      {attachments.length > 0 && (
        <div className="rh-attachments">
          {attachments.map((f, i) => (
            <span key={`${f.name}-${f.size}-${i}`} className="rh-attach-chip">
              <i className="bi bi-file-earmark-text" aria-hidden="true" />
              <span className="rh-attach-name">{f.name}</span>
              <button
                type="button"
                onClick={() => removeFile(i)}
                disabled={parsing}
                aria-label={`Remove ${f.name}`}
                className="rh-attach-remove"
              >
                <i className="bi bi-x" aria-hidden="true" />
              </button>
            </span>
          ))}
        </div>
      )}
      {attachError && <p className="rh-error">{attachError}</p>}

      <div className="rh-chips">
        {SEARCH_CHIPS.map((c) => (
          <button
            key={c.label}
            type="button"
            className="rh-chip"
            onClick={() => void runMandate(c.prompt, c.path)}
            disabled={parsing}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="rh-or" role="separator">
        <span>OR</span>
      </div>

      <button
        type="button"
        className="rh-diy btn-unstyled"
        onClick={() => {
          const card = document.querySelector<HTMLButtonElement>('.rh-qs-card');
          card?.focus();
          card?.scrollIntoView({ block: 'nearest' });
        }}
      >
        <i className="bi bi-sliders" aria-hidden="true" />
        Set it up yourself!
      </button>
    </section>
  );
}
